/**
 * useClaimPoller — W1.7
 *
 * Pollt GET /api/v2/claims/:id alle 3 Sekunden.
 * Stoppt automatisch bei 'confirmed', 'failed' oder nach MAX_ATTEMPTS (40 = ~2 Minuten).
 * Cleanup via useEffect-Return verhindert Memory-Leaks bei Navigation.
 */
import { useState, useEffect, useRef } from 'react'
import { claimApi, type ClaimStatus } from '../api/claimApi'

export const MAX_ATTEMPTS = 40
export const INTERVAL_MS = 3000

type UseClaimPollerResult = {
  status: ClaimStatus | null
  txSignature: string | undefined
  failureReason: string | undefined
  timedOut: boolean
  attempts: number
  resetPoller: () => void
}

export function useClaimPoller(claimId: string | null): UseClaimPollerResult {
  const [status, setStatus] = useState<ClaimStatus | null>(null)
  const [txSignature, setTxSignature] = useState<string | undefined>(undefined)
  const [failureReason, setFailureReason] = useState<string | undefined>(undefined)
  const [timedOut, setTimedOut] = useState(false)
  const attemptsRef = useRef(0)
  const [attempts, setAttempts] = useState(0)

  const resetPoller = () => {
    attemptsRef.current = 0
    setAttempts(0)
    setStatus(null)
    setTxSignature(undefined)
    setFailureReason(undefined)
    setTimedOut(false)
  }

  useEffect(() => {
    if (!claimId) return
    if (status === 'confirmed' || status === 'failed' || timedOut) return

    const interval = setInterval(async () => {
      attemptsRef.current += 1
      setAttempts(attemptsRef.current)

      if (attemptsRef.current > MAX_ATTEMPTS) {
        setTimedOut(true)
        clearInterval(interval)
        return
      }

      const { data } = await claimApi.getStatus(claimId)
      if (data?.status) {
        setStatus(data.status)
        if (data.tx_signature) setTxSignature(data.tx_signature)
        if (data.failure_reason) setFailureReason(data.failure_reason)
      }
    }, INTERVAL_MS)

    return () => clearInterval(interval)
  }, [claimId, status, timedOut])

  return { status, txSignature, failureReason, timedOut, attempts, resetPoller }
}
